import { computed, makeObservable, override } from 'mobx';

import {
  ChannelKind,
  ChannelStatus,
  ChannelWazzupServer,
  IChannelBase,
  IWazzupChannelBasic,
  IWazzupChannelModel,
  mapWzOAuthErrorToMessage,
  mapWzRevokedReasonToMessage,
  normalizeWazzupChannel,
  WzConnectionKind,
  WzOAuthError
} from 'shared/entities/channels';
import { apiUrls } from 'shared/entities/domain';
import { IRootStore } from 'shared/entities/store/rootStore';
import { FieldModel } from 'shared/models/form';

import ListModel from '../../ListModel';
import ChannelBaseModel from '../ChannelBaseModel';
import ChannelTokenModel from '../ChannelTokenModel';

type WazzupChannelModelParams = Omit<IChannelBase, 'tokens' | 'scenarioIds'> &
  Omit<IWazzupChannelBasic, 'tokens'> & {
    rootStore: IRootStore;
    tokens: ListModel<ChannelTokenModel>;
    scenarioIds: FieldModel<string[]>;
  };

export default class WazzupChannelModel
  extends ChannelBaseModel
  implements IWazzupChannelModel
{
  readonly kind: ChannelKind.WZ = ChannelKind.WZ;
  readonly wzConnectionKind: WzConnectionKind;
  readonly wzRevokedReason: IWazzupChannelBasic['wzRevokedReason'];
  readonly wzRoutingState: IWazzupChannelBasic['wzRoutingState'];
  readonly wzMigrationAvailable: boolean;

  constructor({
    wzConnectionKind,
    wzRevokedReason,
    wzRoutingState,
    wzMigrationAvailable,
    ...params
  }: WazzupChannelModelParams) {
    super(params);

    this.wzConnectionKind = wzConnectionKind;
    this.wzRevokedReason = wzRevokedReason;
    this.wzRoutingState = wzRoutingState;
    this.wzMigrationAvailable = wzMigrationAvailable;

    makeObservable(this, {
      isWzOAuth: computed,
      canMigrateToOAuth: computed,
      revokedReason: computed,
      hasNoTokens: override,
      hasTokenErrors: override,
      remove: override
    });
  }

  get isWzOAuth(): boolean {
    return this.wzConnectionKind === WzConnectionKind.oauth;
  }

  /** Канал на ключе, которому бэк предлагает переход, и он не занят другой операцией */
  get canMigrateToOAuth(): boolean {
    return (
      !this.isWzOAuth &&
      this.wzMigrationAvailable &&
      this.wzRoutingState === null
    );
  }

  get revokedReason(): string | null {
    if (!this.isWzOAuth || !this.hasTokenErrors) {
      return null;
    }

    return mapWzRevokedReasonToMessage(this.wzRevokedReason)(
      this.rootStore.translationsStore.t
    );
  }

  get hasTokenErrors(): boolean {
    return this.isWzOAuth
      ? this.status === ChannelStatus.revoked
      : this.hasTokenErrorsByTokens();
  }

  get hasNoTokens(): boolean {
    return !this.isWzOAuth && this.hasNoTokensByTokens();
  }

  async remove(): Promise<BaseResponse> {
    if (this.removingStage.isLoading) {
      return {
        isError: true
      };
    }

    this.removingStage.loading();
    this.rootStore.channelsStore.remove(this.id);

    const { isError, data } = await this.rootStore.networkStore.api<
      {},
      WzOAuthError
    >(apiUrls.CHANNELS_REMOVE, {
      method: 'POST',
      data: {
        _id: this.id
      },
      errorsMap: (code) =>
        code === WzOAuthError.operationInProgress
          ? mapWzOAuthErrorToMessage(code)
          : () => ''
    });

    isError ? this.removingStage.error() : this.removingStage.success();

    if (
      isError &&
      data &&
      'code' in data &&
      data.code === WzOAuthError.operationInProgress
    ) {
      await this.rootStore.channelsStore.refresh();
    }

    return {
      isError
    };
  }

  static fromJson(
    raw: ChannelWazzupServer,
    rootStore: IRootStore
  ): WazzupChannelModel {
    const collection = this.getCollectionTokens(raw, rootStore);

    return new WazzupChannelModel({
      ...normalizeWazzupChannel(raw),
      linked: raw.manager_linked,
      tokens: new ListModel<ChannelTokenModel>(collection),
      scenarioIds: new FieldModel<string[]>(raw.scenario_ids),
      rootStore
    });
  }
}
